// Personel › Çalışan Performansı — randevu, iptal, tamamlama ve sistem işlemleri.
// Analiz sekmesi dili (design-principles §4): KPI satırı + sabit gövdeli kartlar.
import React, { useEffect } from "react";
import { Box, Typography, Avatar, Skeleton, alpha } from "@mui/material";
import {
  People as PeopleIcon,
  EventAvailable as EventIcon,
  TouchApp as ActionIcon,
  GroupRemove as GroupOffIcon,
} from "@mui/icons-material";
import { useLazyGetPerformanceStaffQuery } from "../../store/dashboardApi";
import { useClinicTheme } from "../../theme";
import { COLORS, TYPO, CARD_GAP } from "../../styles/uiConstants";
import KpiTile from "../../components/molecules/KpiTile";
import {
  AnalyticsCard,
  RankBarList,
  BodySkeleton,
  CardEmpty,
  nf,
  pct,
} from "../../components/organisms/charts/analyticsParts";
import { avatarTone, initialsOf } from "../Secretary/PatientsPage/patientListFormat";
import {
  PerformanceTabFrame,
  usePreviewState,
  kpiGridSx,
  scrollAreaSx,
} from "./performanceTabParts";

// Ad ve soyad sunucudan tek alanda gelir ("Ayşe Yılmaz"); avatar için ayrıştır.
const splitName = (full) => {
  const parts = String(full || "").trim().split(/\s+/);
  return { name: parts[0] || "", surname: parts.length > 1 ? parts[parts.length - 1] : "" };
};

// Satır ızgarası: kişi · randevu · iptal · tamamlama · işlem
const ROW_GRID = "minmax(160px, 1fr) 72px 72px 168px 72px";

const numSx = {
  fontSize: TYPO.body,
  color: COLORS.slate700,
  fontVariantNumeric: "tabular-nums",
  textAlign: "right",
};

function completionColor(rate) {
  if (rate >= 80) return COLORS.successDark;
  if (rate >= 50) return COLORS.warningDark;
  return COLORS.errorDark;
}

function StaffHeader() {
  const cell = (label, align = "right") => (
    <Typography
      sx={{
        fontSize: TYPO.caption,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: COLORS.slate500,
        textAlign: align,
      }}
    >
      {label}
    </Typography>
  );
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: ROW_GRID,
        gap: 2,
        pb: 1,
        borderBottom: `1px solid ${COLORS.slate100}`,
        position: "sticky",
        top: 0,
        bgcolor: COLORS.white,
        zIndex: 1,
      }}
    >
      {cell("Çalışan", "left")}
      {cell("Randevu")}
      {cell("İptal")}
      {cell("Tamamlama", "left")}
      {cell("İşlem")}
    </Box>
  );
}

function StaffRow({ row }) {
  const person = splitName(row.name);
  const tone = avatarTone(person);
  const total = row.appointmentTotal ?? row.appointmentCount ?? 0;
  const rate = row.completionRate ?? 0;
  const color = completionColor(rate);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: ROW_GRID,
        alignItems: "center",
        gap: 2,
        py: 1.25,
        borderBottom: `1px solid ${COLORS.slate100}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha(tone, 0.14),
            color: tone,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {initialsOf(person)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}>
            {row.name || "—"}
          </Typography>
          <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
            {row.roleTemplate || row.title || "Rol atanmamış"}
          </Typography>
        </Box>
      </Box>
      <Typography sx={numSx}>{nf(total)}</Typography>
      <Typography sx={{ ...numSx, color: row.appointmentCancelled ? COLORS.slate700 : COLORS.slate300 }}>
        {row.appointmentCancelled ? nf(row.appointmentCancelled) : "—"}
      </Typography>
      {total ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: COLORS.slate100, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${Math.min(rate, 100)}%`,
                borderRadius: 2,
                bgcolor: color,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: TYPO.body,
              color: COLORS.slate700,
              fontVariantNumeric: "tabular-nums",
              minWidth: 44,
              textAlign: "right",
            }}
          >
            %{nf(rate)}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>Randevu yok</Typography>
      )}
      <Typography sx={numSx}>{nf(row.totalActions)}</Typography>
    </Box>
  );
}

function StaffListSkeleton() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
      {[...Array(6)].map((_, i) => (
        <Box key={i} sx={{ display: "grid", gridTemplateColumns: ROW_GRID, gap: 2, alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width={`${50 + ((i * 13) % 30)}%`} />
              <Skeleton width="35%" sx={{ fontSize: TYPO.caption }} />
            </Box>
          </Box>
          <Skeleton width={28} sx={{ justifySelf: "end" }} />
          <Skeleton width={20} sx={{ justifySelf: "end" }} />
          <Skeleton variant="rounded" height={4} />
          <Skeleton width={28} sx={{ justifySelf: "end" }} />
        </Box>
      ))}
    </Box>
  );
}

export default function StaffPerformanceTab({ token, period, onPeriodChange }) {
  const [fetch, { data, isLoading, isFetching }] = useLazyGetPerformanceStaffQuery();
  const { palette } = useClinicTheme();
  const preview = usePreviewState();

  useEffect(() => {
    if (token) fetch({ token, period });
  }, [token, period, fetch]);

  const loading = preview === "yukleniyor" || (!preview && (isLoading || isFetching || !data));
  const staffData = preview ? [] : data?.data?.staff || [];
  const summary = preview ? {} : data?.data?.summary || {};

  const sum = (key) => staffData.reduce((s, r) => s + (r[key] || 0), 0);
  const totalAppointments = summary.totalAppointments ?? sum("appointmentTotal");
  const totalCompleted = sum("appointmentCompleted");
  const totalCancelled = sum("appointmentCancelled");
  const totalActions = sum("totalActions");
  const avgCompletion = summary.avgCompletionRate ?? pct(totalCompleted, totalAppointments);
  const activeStaff = staffData.filter((r) => (r.appointmentTotal || 0) + (r.totalActions || 0) > 0).length;

  const byAppointments = [...staffData]
    .filter((r) => r.appointmentTotal)
    .sort((a, b) => b.appointmentTotal - a.appointmentTotal)
    .map((r) => ({ name: r.name || "—", count: r.appointmentTotal }));
  const byActions = [...staffData]
    .filter((r) => r.totalActions)
    .sort((a, b) => b.totalActions - a.totalActions)
    .map((r) => ({ name: r.name || "—", count: r.totalActions }));
  const sortedStaff = [...staffData].sort(
    (a, b) => (b.appointmentTotal || 0) - (a.appointmentTotal || 0) || (b.totalActions || 0) - (a.totalActions || 0),
  );

  return (
    <PerformanceTabFrame
      period={period}
      onPeriodChange={onPeriodChange}
      note="çalışan bazında randevu ve sistem işlemleri"
    >
      <Box sx={kpiGridSx}>
        <KpiTile
          loading={loading}
          label="Çalışan"
          value={nf(summary.totalStaff ?? staffData.length)}
          hint={`${nf(activeStaff)} çalışan bu dönemde aktif`}
        />
        <KpiTile
          loading={loading}
          label="Randevu"
          value={nf(totalAppointments)}
          hint={totalCancelled ? `${nf(totalCancelled)} randevu iptal edildi` : "İptal edilen randevu yok"}
        />
        <KpiTile
          loading={loading}
          label="Ort. tamamlama"
          value={totalAppointments ? `%${nf(avgCompletion)}` : "—"}
          hint={
            totalAppointments
              ? `${nf(totalCompleted)} randevu tamamlandı`
              : "Bu dönemde randevu yok"
          }
        />
        <KpiTile
          loading={loading}
          label="Sistem işlemi"
          value={nf(totalActions)}
          hint="Kayıt, güncelleme ve diğer işlemler"
        />
      </Box>

      <AnalyticsCard
        title="Çalışanlar"
        subtitle="Randevu, iptal, tamamlama oranı ve sistem işlemleri"
        icon={<PeopleIcon />}
        bodyHeight={360}
      >
        {loading ? (
          <StaffListSkeleton />
        ) : !sortedStaff.length ? (
          <CardEmpty
            icon={<GroupOffIcon />}
            title="Çalışan yok"
            description="Kliniğe personel eklendikçe performansları burada listelenir."
          />
        ) : (
          <Box sx={{ ...scrollAreaSx, overflowX: "auto" }}>
            <Box sx={{ minWidth: 620 }}>
              <StaffHeader />
              {sortedStaff.map((row, idx) => (
                <StaffRow key={row.id || row._id || idx} row={row} />
              ))}
            </Box>
          </Box>
        )}
      </AnalyticsCard>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <AnalyticsCard
          title="Randevu yükü"
          subtitle="Çalışan başına randevu sayısı"
          icon={<EventIcon />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={byAppointments}
              color={palette.primary}
              total={totalAppointments}
              unit="randevu"
              emptyText={{
                icon: <EventIcon />,
                title: "Bu dönemde randevu yok",
                description: "Çalışanlara randevu verildikçe dağılım burada görünür.",
              }}
            />
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Sistem işlemleri"
          subtitle="Çalışan başına kayıt ve güncelleme sayısı"
          icon={<ActionIcon />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={byActions}
              color={palette.primary}
              total={totalActions}
              unit="işlem"
              emptyText={{
                icon: <ActionIcon />,
                title: "Bu dönemde işlem yok",
                description: "Çalışanlar sistemde kayıt açıp güncelledikçe burada sıralanır.",
              }}
            />
          )}
        </AnalyticsCard>
      </Box>
    </PerformanceTabFrame>
  );
}
